import React, { useState, useEffect, useCallback } from "react";
import { Paper, Typography, Box, Grid, TextField, Button } from "@mui/material";
import ImageGalleryDropdown from "./ImageGalleryDropdown";
import api from "../services/api";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import RecordNavigator from "../components/RecordNavigator";
import { formatPhoneNumber } from "../utils/phoneUtils";
import { useRefresh } from "../context/RefreshContext";

const CustomerInfoPanel = ({ customerId }) => {
    const [customer, setCustomer] = useState(null);
    const [editedCustomer, setEditedCustomer] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const customers = location.state?.records || [];
    const { triggerRefresh } = useRefresh();

    const fetchCustomerDetails = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await api.get(`/customers/${customerId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCustomer(response.data);
            setEditedCustomer(response.data);
        } catch (error) {
            console.error("Error fetching customer details:", error);
        }
    }, [customerId]); // ✅ Dependency array includes `customerId`

    useEffect(() => {
        fetchCustomerDetails();
      }, [fetchCustomerDetails, triggerRefresh]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedCustomer({ ...editedCustomer, [name]: value });
    };

    const handleEditClick = async () => {
        if (isEditing) {
            try {
                const token = localStorage.getItem("token");
                const updatePayload = {
                    firstName: editedCustomer.firstName,
                    lastName: editedCustomer.lastName,
                    address: editedCustomer.address,
                    phone1: editedCustomer.phone1,
                    phone2: editedCustomer.phone2,
                    email: editedCustomer.email,
                    comments: editedCustomer.comments,
                };

                const response = await api.put(`/customers/${customerId}`, updatePayload, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data) {
                    setCustomer(response.data);
                    setEditedCustomer(response.data);
                }
                setIsEditing(false);
                triggerRefresh();
            } catch (error) {
                console.error("Error updating customer:", error);
            }
        } else {
            setIsEditing(true);
        }
    };

    const handleDeleteClick = () => {
        if (window.confirm("Are you sure you want to delete this customer? This action cannot be undone.")) {
            const token = localStorage.getItem("token");
            api
                .delete(`/customers/${customerId}`, { // ✅ Use customerId
                    headers: { Authorization: `Bearer ${token}` },
                })
                .then(() => {
                    navigate("/customers"); // ✅ Redirect after deletion
                })
                .catch((error) => {
                    console.error("Error deleting customer:", error);
                });
        }
    };

    const disabledInputStyles = {
        "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "black",
        },
    };

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                    Customer Details
                </span>
                <RecordNavigator records={customers} currentId={id} basePath="/customers" />
            </Typography>
            <Box sx={{ maxWidth: "85%", mx: "auto" }}>
                <Grid container spacing={2} justifyContent="center">
                    {/* Row 1: First Name, Last Name, Address */}
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="First Name"
                            name="firstName"
                            value={editedCustomer.firstName || ""}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            disabled={!isEditing}
                            sx={disabledInputStyles}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Last Name"
                            name="lastName"
                            value={editedCustomer.lastName || ""}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            disabled={!isEditing}
                            sx={disabledInputStyles}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Address"
                            name="address"
                            value={editedCustomer.address || ""}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            disabled={!isEditing}
                            sx={disabledInputStyles}
                        />
                    </Grid>
                    {/* Row 2: Phone 1, Phone 2, Email */}
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Phone 1"
                            name="phone1"
                            value={formatPhoneNumber(editedCustomer.phone1) || ""}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            disabled={!isEditing}
                            sx={disabledInputStyles}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Phone 2"
                            name="phone2"
                            value={formatPhoneNumber(editedCustomer.phone2) || ""}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            disabled={!isEditing}
                            sx={disabledInputStyles}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Email"
                            name="email"
                            value={editedCustomer.email || ""}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            disabled={!isEditing}
                            sx={disabledInputStyles}
                        />
                    </Grid>
                    {/* Row 3: Comments and Photo Upload */}
                    <Grid item xs={5}>
                        <TextField
                            label="Comments"
                            name="comments"
                            value={editedCustomer.comments || ""}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                            multiline
                            rows={2}
                            disabled={!isEditing}
                            sx={disabledInputStyles}
                        />
                    </Grid>
                    <Grid item xs={1} sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ImageGalleryDropdown
                            images={customer?.bucket?.images || []} // Use optional chaining
                            onUploadSuccess={(newImageData) => {
                                setCustomer((prev) => ({
                                    ...prev,
                                    bucket: {
                                        ...prev?.bucket,
                                        images: [...(prev?.bucket?.images || []), newImageData],
                                    },
                                }));
                            }}
                            onDelete={(imageId) => {
                                api.delete(`/images/${imageId}`)
                                    .then(() => {
                                        setCustomer((prev) => ({
                                            ...prev,
                                            bucket: {
                                                ...prev?.bucket,
                                                images: prev?.bucket?.images.filter((img) => img.id !== imageId),
                                            },
                                        }));
                                    })
                                    .catch((error) => {
                                        console.error("Error deleting image:", error);
                                    });
                            }}
                            customerId={customer?.id} // Ensure customer exists
                        />
                    </Grid>
                </Grid>
            </Box>
            {/* Action Buttons */}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", maxWidth: "70%", mx: "auto" }}>
                <Button variant="contained" color="primary" onClick={handleEditClick}>
                    {isEditing ? "Save Changes" : "Edit Customer"}
                </Button>
                <Button variant="outlined" color="error" onClick={handleDeleteClick}>
                    Delete Customer
                </Button>
            </Box>
        </Paper>
    );
};

export default CustomerInfoPanel;